import { useState } from "react";

// material-ui
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { useColorScheme } from "@mui/material/styles";

// project imports
import useConfig from "hooks/useConfig";
import { THEME_COLOR_CONFIG, THEME_FONT_OPTIONS, THEME_PRESETS } from "config/themeColors";
import { withAlpha } from "utils/colorUtils";

// assets
import BgColorsOutlined from "@ant-design/icons/BgColorsOutlined";
import CloseOutlined from "@ant-design/icons/CloseOutlined";
import MoonOutlined from "@ant-design/icons/MoonOutlined";
import SunOutlined from "@ant-design/icons/SunOutlined";

// ==============================|| HEADER - THEME CUSTOMIZATION ||============================== //

export default function ThemeCustomize() {
  const [open, setOpen] = useState(false);
  const { state, setField, resetState } = useConfig();
  const { mode, setMode } = useColorScheme();

  const handleMode = (_, value) => {
    if (value !== null) {
      setMode(value);
    }
  };

  const handleResetDefaults = () => {
    resetState();
    setMode("system");
  };

  const handleQuickModeToggle = () => {
    setMode(mode === "dark" ? "light" : "dark");
  };

  const headerIconSx = (theme) => ({
    "--selection-base-bg": withAlpha(
      theme.vars.palette.primary.main,
      theme.palette.mode === "dark"
        ? THEME_COLOR_CONFIG.selection.baseBgAlphaDark
        : THEME_COLOR_CONFIG.selection.baseBgAlphaLight,
    ),
    "--selection-hover-bg": withAlpha(
      theme.vars.palette.primary.main,
      theme.palette.mode === "dark"
        ? THEME_COLOR_CONFIG.selection.hoverBgAlphaDark
        : THEME_COLOR_CONFIG.selection.hoverBgAlphaLight,
    ),
    color: "text.primary",
    bgcolor: "var(--selection-base-bg)",
    "&:hover": {
      bgcolor: "var(--selection-hover-bg)",
      color: "primary.main",
    },
  });

  return (
    <>
      <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0 }}>
        <IconButton
          color="secondary"
          aria-label="quick theme mode toggle"
          onClick={handleQuickModeToggle}
          sx={headerIconSx}
        >
          {mode === "dark" ? <SunOutlined /> : <MoonOutlined />}
        </IconButton>
        <IconButton
          color="secondary"
          aria-label="theme customization"
          onClick={() => setOpen(true)}
          sx={headerIconSx}
        >
          <BgColorsOutlined />
        </IconButton>
      </Stack>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{ sx: { width: { xs: "100%", sm: 380 } } }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: "divider" }}
          >
            <Typography variant="h5">Theme Customization</Typography>
            <IconButton aria-label="close theme customization" onClick={() => setOpen(false)}>
              <CloseOutlined />
            </IconButton>
          </Stack>

          <Box sx={{ p: 2.5, flex: 1, overflow: "auto" }}>

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Color presets
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 1.25,
              mb: 2,
            }}
          >
            {THEME_PRESETS.map((p) => {
              const selected = state.presetColor === p.key;
              return (
                <Button
                  key={p.id}
                  size="small"
                  variant="outlined"
                  onClick={() => setField("presetColor", p.key)}
                  sx={(theme) => ({
                    "--selection-base-bg": withAlpha(
                      theme.vars.palette.primary.main,
                      theme.palette.mode === "dark"
                        ? THEME_COLOR_CONFIG.selection.baseBgAlphaDark
                        : THEME_COLOR_CONFIG.selection.baseBgAlphaLight,
                    ),
                    "--selection-hover-bg": withAlpha(
                      theme.vars.palette.primary.main,
                      theme.palette.mode === "dark"
                        ? THEME_COLOR_CONFIG.selection.hoverBgAlphaDark
                        : THEME_COLOR_CONFIG.selection.hoverBgAlphaLight,
                    ),
                    flexDirection: "column",
                    py: 1.1,
                    gap: 0.5,
                    textTransform: "none",
                    borderColor: selected ? "primary.main" : "divider",
                    bgcolor: "var(--selection-base-bg)",
                    color: selected ? "primary.main" : "text.primary",
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: "var(--selection-hover-bg)",
                      color: "primary.main",
                      borderColor: selected ? "primary.main" : "primary.light",
                      boxShadow: "none",
                    },
                  })}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 32,
                      borderRadius: 1,
                      bgcolor: p.swatch,
                      border: 1,
                      borderColor: selected ? "primary.main" : "divider",
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: selected ? "primary.main" : "text.primary",
                      transition: "color 120ms ease",
                      ".MuiButton-root:hover &": {
                        color: "primary.main",
                      },
                    }}
                  >
                    {p.label}
                  </Typography>
                </Button>
              );
            })}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Layout width
          </Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={state.layout}
            onChange={(_, v) => v && setField("layout", v)}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="fluid">Fluid</ToggleButton>
            <ToggleButton value="container">Container</ToggleButton>
          </ToggleButtonGroup>

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Font family
          </Typography>
          <Stack
            direction="row"
            flexWrap="wrap"
            useFlexGap
            spacing={1}
            sx={{ mb: 2 }}
          >
            {THEME_FONT_OPTIONS.map((f) => (
              <Button
                key={f.id}
                size="small"
                variant={
                  state.fontFamily === f.stack ? "contained" : "outlined"
                }
                onClick={() => setField("fontFamily", f.stack)}
                sx={{ minWidth: "calc(50% - 8px)", textTransform: "none" }}
              >
                <Stack alignItems="center" spacing={0.25}>
                  <Typography variant="body2" sx={{ fontFamily: f.stack }}>
                    Aa
                  </Typography>
                  <Typography variant="caption" color="inherit">
                    {f.label}
                  </Typography>
                </Stack>
              </Button>
            ))}
          </Stack>

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Theme mode
          </Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={mode ?? "system"}
            onChange={handleMode}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="light">Light</ToggleButton>
            <ToggleButton value="dark">Dark</ToggleButton>
            <ToggleButton value="system">System</ToggleButton>
          </ToggleButtonGroup>

          <Divider sx={{ my: 2 }} />

          <Button variant="outlined" fullWidth onClick={handleResetDefaults}>
            Apply Reset To Default
          </Button>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 2 }}
          >
            Theme choices are stored in session storage for this browser tab.
          </Typography>
        </Box>
        </Box>
      </Drawer>
    </>
  );
}
